import React, { useState, useEffect, useCallback } from "react";
import { API_BASE, authHeaders } from "./AgencyContext";

const STATUS_BADGE = {
  active: { bg: "#E1F5EE", color: "#0F6E56" },
  invited: { bg: "#f3f2ef", color: "#666" },
  suspended: { bg: "#FAEEDA", color: "#633806" },
  removed: { bg: "#FAE4E4", color: "#8B1A1A" },
};

const ROLE_LABEL = { owner: "Owner", admin: "Admin", manager: "Manager", viewer: "Viewer" };

export default function AgencyMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "manager" });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/agency/members`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setMembers(d.members || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function invite(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    setMsg(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/agency/members`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (r.ok) {
        setMsg({ type: "ok", text: `Invite sent to ${form.email}` });
        setForm({ name: "", email: "", role: "manager" });
        setShowInvite(false);
        load();
      } else {
        setMsg({ type: "err", text: d.error || "Failed" });
      }
    } catch {
      setMsg({ type: "err", text: "Network error" });
    }
    setSubmitting(false);
  }

  async function removeMember(id, name) {
    if (!window.confirm(`Remove ${name} from the agency?`)) return;
    try {
      const r = await fetch(`${API_BASE}/app/api/agency/members/${id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      if (r.ok) load();
    } catch { /* ignore */ }
  }

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 500 }}>Team Members</div>
          <div style={{ fontSize: 13, color: "#888" }}>{members.length} member{members.length !== 1 ? "s" : ""}</div>
        </div>
        <button onClick={() => setShowInvite(!showInvite)}
          style={{ background: "#0077B5", color: "#fff", border: "none", padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          + Invite Member
        </button>
      </div>

      {msg && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.type === "ok" ? "#E1F5EE" : "#FAE4E4", color: msg.type === "ok" ? "#0F6E56" : "#8B1A1A" }}>
          {msg.text}
        </div>
      )}

      {showInvite && (
        <form onSubmit={invite} style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Invite a team member</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, marginBottom: 12 }}>
            <input placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }} />
            <input placeholder="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }} />
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }}>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <button type="submit" disabled={submitting}
            style={{ background: "#0077B5", color: "#fff", border: "none", padding: "8px 20px", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: submitting ? 0.6 : 1 }}>
            {submitting ? "Sending..." : "Send Invite"}
          </button>
        </form>
      )}

      <div style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, overflow: "hidden" }}>
        {members.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: "#888" }}>No team members yet.</div>
        ) : (
          members.map((m, i) => {
            const st = STATUS_BADGE[m.status] || STATUS_BADGE.invited;
            return (
              <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px",
                borderBottom: i < members.length - 1 ? "0.5px solid rgba(0,0,0,0.05)" : "none" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{m.name}</div>
                  <div style={{ fontSize: 11, color: "#888" }}>{m.email}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, background: "#f3f2ef", color: "#666", padding: "2px 8px", borderRadius: 12 }}>
                    {ROLE_LABEL[m.role] || m.role}
                  </span>
                  <span style={{ fontSize: 11, background: st.bg, color: st.color, padding: "2px 8px", borderRadius: 12, fontWeight: 600 }}>
                    {m.status}
                  </span>
                  {m.role !== "owner" && m.status !== "removed" && (
                    <button onClick={() => removeMember(m.id, m.name)}
                      style={{ background: "none", border: "none", color: "#D85A30", fontSize: 11, cursor: "pointer", padding: "2px 6px" }}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
